import { IconButton, Stack, Typography, useTheme } from "@mui/material";
import {
  DeleteIcon,
  EditIcon,
  Horizontal3DotsIcon,
  PinIcon,
  UnPinIcon,
} from "components/icons";
import { DropdownMenu } from "components/menu";
import { useState } from "react";
import { ChatRowTitleEditor } from "./ChatRowTitleEditor";
import type { ChatRowAction } from "./ChatSidebarItem";

const ICON_SX = { fontSize: "18px" };

const TITLE_MAX_WIDTH = "75%";

const menuOptions = (isPinned: boolean) => [
  { name: "Rename", value: "rename", icon: <EditIcon sx={ICON_SX} /> },
  isPinned
    ? { name: "Unpin chat", value: "unpin", icon: <UnPinIcon sx={ICON_SX} /> }
    : { name: "Pin chat", value: "pin", icon: <PinIcon sx={ICON_SX} /> },
  { name: "Delete", value: "delete", icon: <DeleteIcon sx={ICON_SX} /> },
];

const isChatRowAction = (value: string | undefined): value is ChatRowAction =>
  value === "rename" ||
  value === "pin" ||
  value === "unpin" ||
  value === "delete";

interface ChatPanelHeaderProps {
  /** Display label — the thread's title, or its first question as a fallback. */
  title: string;
  /** Stored title. Null opens the rename editor empty. */
  rawTitle: string | null;
  isPinned: boolean;
  /** False before the `conversation` event lands — nothing to act on yet. */
  canAct: boolean;
  onAction: (action: ChatRowAction) => void;
  onRename: (title: string) => void;
}

/**
 * Names the open thread above the messages and carries its overflow menu.
 * Truncation is CSS, not a slice, so the title uses whatever width the panel
 * has and stays whole in the tooltip.
 *
 * Rename state is local rather than the sidebar's atom: the two surfaces edit
 * independently, and one shared atom would put both into edit mode at once.
 */
export const ChatPanelHeader = ({
  title,
  rawTitle,
  isPinned,
  canAct,
  onAction,
  onRename,
}: ChatPanelHeaderProps) => {
  const theme = useTheme();
  const [isRenaming, setIsRenaming] = useState(false);

  const handleAction = (action: ChatRowAction) => {
    if (action === "rename") {
      setIsRenaming(true);
      return;
    }
    onAction(action);
  };

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        px: "16px",
        pb: "8px",
        height: "36px",
        flexShrink: 0,
      }}
    >
      {isRenaming ? (
        <ChatRowTitleEditor
          initialTitle={rawTitle}
          onCommit={(next) => {
            setIsRenaming(false);
            onRename(next);
          }}
          onCancel={() => setIsRenaming(false)}
        />
      ) : (
        <Typography
          variant="subtitle1"
          title={title}
          sx={{
            maxWidth: TITLE_MAX_WIDTH,
            minWidth: 0,
            color: theme.palette.lightBg.high,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
      )}

      {canAct && !isRenaming && (
        <DropdownMenu
          options={menuOptions(isPinned)}
          anchorOriginHorizontal="right"
          onOptionClick={({ value }) => {
            if (isChatRowAction(value)) handleAction(value);
          }}
        >
          <IconButton
            aria-label="Chat options"
            sx={{ p: "4px", color: theme.palette.lightBg.medium }}
          >
            <Horizontal3DotsIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </DropdownMenu>
      )}
    </Stack>
  );
};
